import React from 'react';

function InfoTemplate({ title, message, styling, content }) {
  // ✅ STAGE 1 FIX: Use VITE_API_URL from env
  const API_URL = import.meta.env.VITE_API_URL || window.location.origin;
  const fontFamily = styling?.fontFamily || 'Inter';
  const bgColor = styling?.backgroundColor || '#3b82f6';
  const accentColor = styling?.accentColor || '#60a5fa';
  const textColor = styling?.textColor || '#FFFFFF';
  const mainImage = styling?.mainImage || styling?.announcementImage;
  
  // Editable content with defaults
  const infoBadge = content?.infoBadge || '';
  
  // ✅ STAGE 4: QR Code settings
  const showQR = content?.ctaEnabled === true;
  const whatsappNumber = '2349055444495';
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https://wa.me/${whatsappNumber}`;

  // ✅ Check if media is video
  const isVideo = mainImage && (
    mainImage.endsWith('.mp4') ||
    mainImage.endsWith('.webm') ||
    mainImage.endsWith('.ogg')
  );

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 100%)`,
        fontFamily: `${fontFamily}, sans-serif`,
        padding: '2.5rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decorative Circles */}
      <div
        style={{
          position: 'absolute',
          top: '-15%',
          right: '-10%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.06)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-20%',
          left: '-15%',
          width: '700px',
          height: '700px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
        }}
      />

      {/* Corner Decorative Lines */}
      <svg
        style={{
          position: 'absolute',
          top: '3rem',
          left: '3rem',
          width: '100px',
          height: '100px',
          opacity: 0.3,
        }}
        viewBox="0 0 100 100"
      >
        <path
          d="M 0 0 L 100 0 L 100 15 L 15 15 L 15 100 L 0 100 Z"
          fill="white"
        />
      </svg>

      {/* Top Badge */}
{infoBadge && (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.8rem',
      padding: '0.8rem 2rem',
      background: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      borderRadius: '50px',
      marginBottom: '1.5rem',
      border: '2px solid rgba(255, 255, 255, 0.2)',
    }}
  >
    <div
      style={{
        width: '40px',
        height: '40px',
        background: 'rgba(255, 255, 255, 0.25)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
      }}
    >
      ℹ️
    </div>
    <span
      style={{
        fontSize: '1.1rem',
        fontWeight: '700',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        color: textColor,
      }}
    >
      {infoBadge}
    </span>
  </div>
)}

      {/* Main Title */}
      <h1
        style={{
          fontSize: styling?.fontSize === 'small' ? '2.8rem' : styling?.fontSize === 'large' ? '4.2rem' : '3.5rem',
          fontWeight: '800',
          textAlign: 'center',
          color: textColor,
          marginBottom: '2rem',
          maxWidth: '1000px',
          lineHeight: '1.2',
          letterSpacing: '-0.01em',
          textShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {title || 'Important Information'}
      </h1>

      {/* Image/Video Section */}
      {mainImage && (
        <div
          style={{
            position: 'relative',
            marginBottom: '3rem',
            maxWidth: '700px',
            width: '100%',
          }}
        >
          {/* Decorative Frame */}
          <div
            style={{
              position: 'absolute',
              top: '-15px',
              left: '-15px',
              right: '15px',
              bottom: '15px',
              border: `3px solid ${accentColor}`,
              borderRadius: '20px',
              opacity: 0.4,
            }}
          />
          
          <div
            style={{
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {isVideo ? (
              <video
                src={`${API_URL}${mainImage}`}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            ) : (
              <img
                src={`${API_URL}${mainImage}`}
                alt="Information"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Message Box */}
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(15px)',
          borderRadius: '25px',
          padding: '2.5rem 3rem',
          maxWidth: '900px',
          width: '100%',
          boxShadow: '0 15px 50px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Decorative Corner Accent */}
        <div
          style={{
            position: 'absolute',
            top: '0',
            right: '0',
            width: '80px',
            height: '80px',
            background: accentColor,
            opacity: 0.2,
            borderTopRightRadius: '25px',
            borderBottomLeftRadius: '100%',
          }}
        />

        <p
          style={{
            fontSize: '1.8rem',
            lineHeight: '1.6',
            fontWeight: '500',
            color: textColor,
            textAlign: 'center',
            margin: 0,
          }}
        >
          {message || 'Here is some useful information for you to know'}
        </p>
      </div>

      {/* ✅ STAGE 4: QR Code - Conditional */}
      {showQR && (
        <div
          style={{
            marginTop: '2.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.8rem',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: textColor,
              opacity: 0.9,
            }}
          >
            Scan to Chat
          </div>
          <img
            src={qrCodeUrl}
            alt="WhatsApp QR Code"
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              padding: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            }}
          />
        </div>
      )}

      {/* Side Decorative Lines */}
      <div
        style={{
          position: 'absolute',
          right: '3rem',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
        }}
      >
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            style={{
              width: '80px',
              height: '3px',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '2px',
            }}
          />
        ))}
      </div>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Roboto:wght@400;500;700;900&family=Poppins:wght@400;500;600;700;800;900&family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        `}
      </style>
    </div>
  );
}

export default InfoTemplate;